import { ConfigMessages } from '@MerchantConfigurator/constants/messaging/contents';

export const MessagingStatus = {
  eligible: 'ELIGIBLE',
  ineligible: 'INELIGIBLE',
  notAvailable: 'NOT_AVAILABLE'
};

export const MessagingPlacements = {
  cart: 'cart',
  cart_preview: 'cart_preview',
  category: 'category',
  checkout: 'checkout',
  custom_placement: 'custom_placement',
  home: 'home',
  homepage: 'homepage',
  mini_cart: 'mini_cart',
  product: 'product',
  product_preview: 'product_preview',
  shop: 'shop'
};

export const BannerLayoutPlacements = new Set([
  MessagingPlacements.category,
  MessagingPlacements.home,
  MessagingPlacements.homepage,
  MessagingPlacements.shop
]);

export const UpstreamMessagingPlacementMap = {
  cart: 'cart',
  cart_preview: 'cart',
  category: 'category',
  checkout: 'payment',
  home: 'home',
  homepage: 'home',
  mini_cart: 'cart',
  product: 'product',
  product_preview: 'product',
  shop: 'category'
};

export const PreviewModes = {
  desktop: 'desktop',
  message: 'message',
  mobile: 'mobile'
};

export const PreviewThemes = {
  dark: 'dark',
  light: 'light'
};

export const MessagingAttributes = {
  color: 'color',
  ratio: 'ratio',
  layout: 'layout',
  logoType: 'logo-type',
  logoPosition: 'logo-position',
  placement: 'placement',
  status: 'status',
  textColor: 'text-color',
  textSize: 'text-size'
};

export const ConfigKeys = {
  status: 'status',
  message_reference: 'message_reference'
};

export const ConfigValues = {
  color: {
    black: 'black',
    blue: 'blue',
    gray: 'gray',
    monochrome: 'monochrome',
    white: 'white',
    whiteNoBorder: 'white-no-border'
  },
  layout: {
    banner: 'flex',
    text: 'text'
  },
  logoPosition: {
    left: 'left',
    right: 'right',
    top: 'top'
  },
  logoType: {
    alternative: 'alternative',
    inline: 'inline',
    none: 'none',
    primary: 'primary'
  },
  ratio: {
    '8x1': '8x1',
    '20x1': '20x1'
  },
  status: {
    disabled: 'disabled',
    enabled: 'enabled'
  },
  textColor: {
    black: 'black',
    grayscale: 'grayscale',
    monochrome: 'monochrome',
    white: 'white'
  },
  textSize: {
    small: '10',
    medium: '12',
    large: '16'
  },
  message_reference: ['advanced', 'custom', 'woocommerceBlock', 'insertTag']
};

export const SelectionGroupOptions = {
  color: [
    {
      label: ConfigMessages.color.blue,
      value: ConfigValues.color.blue
    },
    {
      label: ConfigMessages.color.black,
      value: ConfigValues.color.black
    },
    {
      label: ConfigMessages.color.white,
      value: ConfigValues.color.white
    },
    {
      label: ConfigMessages.color.whiteNoBorder,
      value: ConfigValues.color.whiteNoBorder
    }
  ],
  'logo-position': [
    {
      label: ConfigMessages.logoPosition.left,
      value: ConfigValues.logoPosition.left
    },
    {
      label: ConfigMessages.logoPosition.right,
      value: ConfigValues.logoPosition.right
    },
    {
      label: ConfigMessages.logoPosition.top,
      value: ConfigValues.logoPosition.top
    }
  ],
  'logo-type': [
    {
      label: ConfigMessages.logoType.primary,
      value: ConfigValues.logoType.primary
    },
    {
      label: ConfigMessages.logoType.alternative,
      value: ConfigValues.logoType.alternative
    },
    {
      label: ConfigMessages.logoType.inline,
      value: ConfigValues.logoType.inline
    },
    {
      label: ConfigMessages.logoType.none,
      value: ConfigValues.logoType.none
    }
  ],
  ratio: [
    {
      label: ConfigMessages.ratio['20x1'],
      value: ConfigValues.ratio['20x1']
    },
    {
      label: ConfigMessages.ratio['8x1'],
      value: ConfigValues.ratio['8x1']
    }
  ],
  'text-color': [
    {
      label: ConfigMessages.textColor.black,
      value: ConfigValues.textColor.black
    },
    {
      label: ConfigMessages.textColor.white,
      value: ConfigValues.textColor.white
    },
    {
      label: ConfigMessages.textColor.monochrome,
      value: ConfigValues.textColor.monochrome
    },
    {
      label: ConfigMessages.textColor.grayscale,
      value: ConfigValues.textColor.grayscale
    }
  ],
  'text-size': [
    {
      label: ConfigMessages.textSize.small,
      value: ConfigValues.textSize.small
    },
    {
      label: ConfigMessages.textSize.medium,
      value: ConfigValues.textSize.medium
    },
    {
      label: ConfigMessages.textSize.large,
      value: ConfigValues.textSize.large
    }
  ]
};

const defaultMessageConfig = {
  layout: ConfigValues.layout.text,
  'logo-type': ConfigValues.logoType.inline,
  'text-color': ConfigValues.textColor.black,
  'text-size': ConfigValues.textSize.medium
};

const defaultBannerConfig = {
  color: ConfigValues.color.whiteNoBorder,
  layout: ConfigValues.layout.banner,
  ratio: ConfigValues.ratio['8x1']
};

export const defaultConfig = {
  cart: {
    placement: MessagingPlacements.cart,
    status: ConfigValues.status.enabled,
    ...defaultMessageConfig
  },
  cart_preview: {
    placement: MessagingPlacements.cart_preview,
    status: ConfigValues.status.disabled,
    ...defaultMessageConfig
  },
  category: {
    placement: MessagingPlacements.category,
    status: ConfigValues.status.disabled,
    ...defaultBannerConfig
  },
  checkout: {
    placement: MessagingPlacements.checkout,
    status: ConfigValues.status.enabled,
    ...defaultMessageConfig
  },
  custom_placement: {
    status: ConfigValues.status.disabled
  },
  home: {
    placement: MessagingPlacements.home,
    status: ConfigValues.status.disabled,
    ...defaultBannerConfig
  },
  homepage: {
    placement: MessagingPlacements.homepage,
    status: ConfigValues.status.disabled,
    ...defaultBannerConfig
  },
  mini_cart: {
    placement: MessagingPlacements.mini_cart,
    status: ConfigValues.status.disabled,
    ...defaultMessageConfig
  },
  product: {
    placement: MessagingPlacements.product,
    status: ConfigValues.status.enabled,
    ...defaultMessageConfig
  },
  product_preview: {
    placement: MessagingPlacements.product_preview,
    status: ConfigValues.status.disabled,
    ...defaultMessageConfig
  },
  shop: {
    placement: MessagingPlacements.shop,
    status: ConfigValues.status.disabled,
    ...defaultBannerConfig
  }
};

export const MessagingPlacementsOrder = [
  MessagingPlacements.product,
  MessagingPlacements.cart,
  MessagingPlacements.checkout,
  MessagingPlacements.product_preview,
  MessagingPlacements.cart_preview,
  MessagingPlacements.mini_cart,
  MessagingPlacements.home,
  MessagingPlacements.homepage,
  MessagingPlacements.category,
  MessagingPlacements.shop,
  MessagingPlacements.custom_placement
];
